import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.nameRu);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full py-20 md:py-28 bg-background-50">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950"
            style={{ fontFamily: 'var(--font-heading)' }}>
            Категории товаров
          </h2>
          <p className="text-sm text-foreground-500 font-light mt-1 md:mt-0">
            Выберите категорию для быстрого перехода
          </p>
        </div>

        {/* Mobile-friendly dropdown */}
        <div className="relative mb-10 w-full md:max-w-[380px]" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 md:py-3 bg-white border border-background-200 rounded-xl text-foreground-800 font-label text-sm md:text-sm hover:border-background-300 transition-colors duration-200 cursor-pointer active:bg-background-50"
          >
            <span className={selectedCategory ? '' : 'text-foreground-400'}>
              {selectedCategory || 'Выберите категорию'}
            </span>
            <div className="w-5 h-5 flex items-center justify-center">
              {isOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-500 text-lg transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-500 text-lg transition-transform duration-200"></i>
              )}
            </div>
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-background-200 rounded-xl z-50 max-h-[320px] overflow-y-auto shadow-sm">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat)}
                  className="w-full text-left px-5 py-3.5 text-sm text-foreground-700 font-label hover:bg-background-100 active:bg-background-100 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-none whitespace-nowrap"
                >
                  {cat.nameRu}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Image Grid - 2 cols mobile, 3 sm, 4 lg, 5 xl */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group cursor-pointer rounded-2xl overflow-hidden bg-white border border-background-200/70 hover:-translate-y-1 active:scale-[0.98] transition-all duration-300"
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={`${cat.nameRu} — Yoybuy Sheet`}
                  title={`${cat.nameRu} — Yoybuy Spreadsheet`}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="px-3 py-3 md:px-4 md:py-3.5">
                <span className="text-xs md:text-sm font-label text-foreground-700 group-hover:text-foreground-950 transition-colors duration-200">
                  {cat.nameRu}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}